<template>
  <div id="create-judge-page" class="page-container">
    <overlay-loading-progress :LoadingFlag="OverlayLoadingFlag"></overlay-loading-progress>

    <!-- Stunning Header Card -->
    <v-card elevation="20" class="header-card mb-4 overflow-hidden">
      <div class="header-gradient">
        <div class="header-content pa-5">
          <v-row align="center" justify="space-between">
            <v-col cols="12" md="6">
              <div class="d-flex align-center">
                <div class="header-icon-box mr-4">
                  <v-icon size="48" color="white">mdi-account-plus-outline</v-icon>
                </div>
                <div>
                  <h1 class="text-h4 font-weight-bold white--text mb-1 header-title">
                    Create Judge
                  </h1>
                  <p class="text-subtitle-1 white--text mb-0 header-subtitle">
                    <v-icon size="16" color="white" class="mr-1">mdi-information-outline</v-icon>
                    Add a new judge by selecting zone, organization type, judge type, and membership details
                  </p>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="6" class="text-right">
              <div class="d-flex justify-end flex-wrap gap-2">
                <!-- List Button -->
                <router-link to="/jci-award/judges/list" style="text-decoration: none">
                  <v-btn color="#8950FC" elevation="8" rounded large class="action-btn white--text">
                    <v-icon left>mdi-format-list-bulleted</v-icon>
                    VIEW LIST
                  </v-btn>
                </router-link>

                <!-- Refresh Button -->
                <v-btn @click.prevent="refreshPageData" color="rgba(255,255,255,0.2)" elevation="0" rounded large
                  class="action-btn white--text">
                  <v-icon left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <!-- Main Form Card -->
    <v-card elevation="12" class="form-card mb-4">
      <v-card-text class="pa-5">
        <!-- Loading -->
        <div v-if="LoadingFlag" class="text-center py-8">
          <v-progress-circular :size="70" :width="7" color="primary" indeterminate></v-progress-circular>
          <p class="mt-4 text-h6 primary--text">Loading form data...</p>
        </div>

        <!-- Form -->
        <v-form ref="form1" v-model="valid1" lazy-validation v-if="!LoadingFlag">
          <!-- Form Header -->
          <div class="form-header mb-5">
            <v-icon color="primary" size="32" class="mr-2">mdi-form-select</v-icon>
            <span class="text-h5 font-weight-bold primary--text">Judge Information</span>
          </div>

          <!-- Form Fields Section -->
          <div class="form-section mb-4">
            <v-row>
              <!-- Zone -->
              <v-col cols="12" md="4">
                <div class="field-label">
                  <v-icon color="primary" size="18" class="mr-1">mdi-map-marker</v-icon>
                  <span class="text-danger">*</span> Zone
                </div>
                <v-autocomplete :items="ZoneCodeOptions" :rules="ZoneCodeRules" v-model="ZoneCode" outlined dense
                  clearable required placeholder="Select Zone" class="custom-input"
                  prepend-inner-icon="mdi-map-marker-radius"></v-autocomplete>
              </v-col>

              <!-- Organization Type -->
              <v-col cols="12" md="4">
                <div class="field-label">
                  <v-icon color="primary" size="18" class="mr-1">mdi-office-building</v-icon>
                  <span class="text-danger">*</span> Organization Type
                </div>
                <v-autocomplete :items="OrganizationTypeOptions" :rules="OrganizationTypeRules"
                  v-model="OrganizationType" outlined dense clearable placeholder="Select Organization Type"
                  class="custom-input" prepend-inner-icon="mdi-domain"></v-autocomplete>
              </v-col>

              <!-- Judge Type -->
              <v-col cols="12" md="4">
                <div class="field-label">
                  <v-icon color="primary" size="18" class="mr-1">mdi-gavel</v-icon>
                  <span class="text-danger">*</span> Judge Type
                </div>
                <v-autocomplete :items="JudgeTypeOptions" :rules="JudgeTypeRules" v-model="JudgeType" outlined dense
                  clearable placeholder="Select Judge Type" class="custom-input"
                  prepend-inner-icon="mdi-account-tie"></v-autocomplete>
              </v-col>

              <!-- Allocated Zones -->
              <v-col cols="12" md="6">
                <div class="field-label">
                  <v-icon color="primary" size="18" class="mr-1">mdi-map-marker-multiple</v-icon>
                  Allocated Zones (Optional)
                </div>
                <v-autocomplete :items="AllocatedZonesOptions" v-model="AllocatedZones" outlined dense clearable
                  multiple chips small-chips deletable-chips placeholder="Select Allocated Zones" class="custom-input"
                  prepend-inner-icon="mdi-map-marker-check"></v-autocomplete>
              </v-col>

              <!-- Membership ID -->
              <v-col cols="12" md="6">
                <div class="field-label">
                  <v-icon color="primary" size="18" class="mr-1">mdi-card-account-details</v-icon>
                  <span class="text-danger">*</span> Membership ID
                </div>
                <v-text-field v-model="MembershipId" outlined dense clearable placeholder="Enter Membership ID"
                  class="custom-input" prepend-inner-icon="mdi-identifier"></v-text-field>
              </v-col>
            </v-row>

            <!-- Search Button -->
            <v-row class="mt-2">
              <v-col cols="12" class="d-flex justify-center">
                <v-btn @click.prevent="getMemberRecords" :loading="SearchFlag" color="primary" elevation="8" x-large
                  rounded class="search-btn px-8">
                  <v-icon left size="24">mdi-account-search</v-icon>
                  SEARCH MEMBER
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <!-- Member Details Preview -->
          <v-card v-if="member && Object.keys(member).length" elevation="8" class="member-preview-card mt-5">
            <div class="member-card-header pa-4">
              <v-icon color="white" size="28" class="mr-2">mdi-account-circle</v-icon>
              <span class="text-h5 font-weight-bold white--text">Member Details</span>
            </div>

            <v-card-text class="pa-5">
              <v-row>
                <!-- Photo Section -->
                <v-col cols="12" md="3" class="d-flex justify-center align-center">
                  <div class="avatar-wrapper">
                    <v-avatar size="140" class="member-avatar">
                      <img :src="member.Photo" alt="Profile" />
                    </v-avatar>
                    <div class="avatar-badge">
                      <v-icon color="white" size="20">mdi-check-decagram</v-icon>
                    </div>
                  </div>
                </v-col>

                <!-- Member Info -->
                <v-col cols="12" md="9">
                  <div class="member-info-section">
                    <h2 class="text-h4 font-weight-bold primary--text mb-4">
                      {{ member.MemberName }}
                    </h2>

                    <v-row>
                      <v-col cols="12" md="6">
                        <div class="info-card mb-3">
                          <v-icon color="primary" class="mr-2">mdi-identifier</v-icon>
                          <div>
                            <div class="info-label">Membership ID</div>
                            <div class="info-value">{{ member.MembershipId }}</div>
                          </div>
                        </div>

                        <div class="info-card mb-3">
                          <v-icon color="primary" class="mr-2">mdi-briefcase</v-icon>
                          <div>
                            <div class="info-label">Designation</div>
                            <div class="info-value">{{ member.Designation }}</div>
                          </div>
                        </div>

                        <div class="info-card mb-3">
                          <v-icon color="primary" class="mr-2">mdi-whatsapp</v-icon>
                          <div>
                            <div class="info-label">WhatsApp No</div>
                            <div class="info-value">{{ member.WhatsappNo }}</div>
                          </div>
                        </div>
                      </v-col>

                      <v-col cols="12" md="6">
                        <div class="info-card mb-3">
                          <v-icon color="primary" class="mr-2">mdi-card-account-details-outline</v-icon>
                          <div>
                            <div class="info-label">Aadhaar Status</div>
                            <v-chip :color="member.AadhaarTextColor" dark small class="mt-1">
                              <v-icon small left>mdi-check-circle</v-icon>
                              {{ member.AadhaarVerifiedTxt }}
                            </v-chip>
                          </div>
                        </div>

                        <div class="info-card mb-3">
                          <v-icon color="primary" class="mr-2">mdi-currency-inr</v-icon>
                          <div>
                            <div class="info-label">Due Count</div>
                            <v-chip :color="member.MemberDueCountFlag ? 'red darken-2' : 'green darken-2'" dark small
                              class="mt-1">
                              <v-icon small left>mdi-alert-circle</v-icon>
                              {{ member.MemberDueCount }}
                            </v-chip>
                          </div>
                        </div>

                        <div class="info-card mb-3">
                          <v-icon color="primary" class="mr-2">mdi-map-marker</v-icon>
                          <div>
                            <div class="info-label">Address</div>
                            <div class="info-value">{{ member.AddressTxt }}</div>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Action Buttons -->
          <v-row class="mt-6">
            <v-col cols="12" class="d-flex justify-center gap-3">
              <v-btn :disabled="!isMemberValid" @click.prevent="submitForm" :loading="SubmitFlag" color="success"
                elevation="8" x-large rounded class="px-10 submit-btn">
                <v-icon left size="24">mdi-check-circle</v-icon>
                SUBMIT
              </v-btn>

              <v-btn @click.prevent="resetAll" color="error" elevation="8" x-large rounded class="px-10 clear-btn">
                <v-icon left size="24">mdi-close-circle</v-icon>
                CLEAR
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {},
  data() {
    return {
      MembershipId: "",
      search: "",
      ProgessStart: 0,
      uploadPercentage: 0,
      SearchFlag: false,
      SelectFlag: true,
      DeleteFlag: false,
      ResultFlag: 0,
      member: {},
      SubmitFlag: false,
      valid1: true,
      LoadingFlag: false,

      SuccessMessage: "",
      ErrorMessage: "",
      OverlayLoadingFlag: false,

      Message: "",
      MessageColor: "",

      alert: {},

      valid: true,

      RoleId: "",

      SampleFilePath: "",

      UploadedFile: {},
      UploadedFileRules: [(v) => !!v || "Upload Excel File is required"],

      OrganizationTypeRules: [(v) => !!v || "Organization Type is required"],
      OrganizationType: "",
      OrganizationTypeOptions: [],

      JudgeTypeRules: [(v) => !!v || "Judge Type is required"],
      JudgeType: "",
      JudgeTypeOptions: [
        {
          value: 174,
          text: "Zone Head Table",
        },
        {
          value: 175,
          text: "National Head Table",
        },
        {
          value: 196,
          text: "EO",
        },
        {
          value: 173,
          text: "Judge",
        },
        {
          value: 312,
          text: "OTS Chairman",
        },
        {
          value: 313,
          text: "PA Chairman",
        },
        {
          value: 317,
          text: "National Nomination Committee Chairman",
        },
      ],

      ZoneCodeRules: [(v) => !!v || "Zone is required"],
      ZoneCode: "",
      ZoneCodeOptions: [],

      AllocatedZones: [],
      AllocatedZonesOptions: [],
      AllocatedZonesRules: [
        (v) => !!v.length || "Select at least one Allocated Zone",
      ],

      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,

      recordData: {},

      dialog: false,
      dialogDelete: false,
      rows: [],
      tableData1: [],
      tableOptions1: [],
      tableColumns1: [],
      TotalMembers: 0,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      FloatingButtonOptions: {},
    };
  },
  computed: {
    isMemberValid() {
      return this.member && Object.keys(this.member).length > 0;
    },
  },
  watch: {
    MembershipId(newVal, oldVal) {
      this.member = {}; // clear previous member details
    },
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }

        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.FloatingButtonOptions = tr.FloatingButtonOptions;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;

        var server_url = companyConfig.apiURL;
        console.log("server_url = " + server_url);
        var SampleFilePath =
          server_url + "assets/sample/head_table_upload_sample.xlsx";
        console.log("SampleFilePath = " + SampleFilePath);
        this.SampleFilePath = SampleFilePath;

        this.getOrganizationTypeOptions();
        this.getZoneCodeOptions();
        this.getAllocatedZonesOptions();
      }
    },
    OrganizationTypeOptions: function () {
      console.log("watch OrganizationTypeOptions");
      this.OrganizationTypeOptionsLoading = false;
    },
    ZoneCodeOptions: function () {
      console.log("watch ZoneCodeOptions");
      this.ZoneCodeOptionsLoading = false;
    },
  },
  created() { },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "award_judges",
        Action: "create",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    resetUploadFile() {
      console.log("resetUploadFile called");
      this.UploadedFile = {};
      this.ErrorMessage = "";
      this.ErrorMessageTxt = {};
      this.SuccessMessage = "";
      this.MessageColor = "";
      this.Message = "";
    },
    resetAll() {
      console.log("resetAll called");
      this.UploadedFile = {};
      this.ErrorMessage = "";
      this.ErrorMessageTxt = {};
      this.SuccessMessage = "";
      this.MessageColor = "";
      this.Message = "";
      this.OrganizationType = "";
      this.JudgeType = "";
      this.ZoneCode = "";
      this.MembershipId = "";
      this.member = {};
    },

    getOrganizationTypeOptions() {
      console.log("getOrganizationTypeOptions called");
      var selectbox1_source = "OrganizationType";
      var selectbox1_destination = "OrganizationTypeOptions";
      var selectbox1_url = "api/organization-type/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getAllocatedZonesOptions() {
      console.log("getAllocatedZonesOptions called");
      var selectbox1_source = "AllocatedZones";
      var selectbox_destination = "AllocatedZonesOptions";
      var selectbox1_url = "api/zone/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
        NotAvailable: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },

    getZoneCodeOptions() {
      console.log("getZoneCodeOptions called");
      var selectbox1_source = "ZoneCode";
      var selectbox1_destination = "ZoneCodeOptions";
      var selectbox1_url = "api/zone/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
        NotAvailable: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    splitSentences(msg) {
      if (!msg) return [];
      return msg.split(/(?<=[.?!])\s+/).filter(Boolean);
    },
    submitForm() {
      console.log("submitForm called");

      if (
        this.MembershipId &&
        this.member &&
        this.OrganizationType &&
        this.JudgeType &&
        this.ZoneCode &&
        this.AllocatedZones
      ) {
        var upload = {
          UserInterface: 1,
          OrganizationType: this.OrganizationType,
          Role: this.JudgeType,
          Membership: this.MembershipId,
          ZoneCode: this.ZoneCode,
          AllocatedZones: this.AllocatedZones,
        };
        console.log("upload=" + JSON.stringify(upload));

        this.progessStart = 1;
        this.SubmitFlag = true;
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/judge/store";
        console.log(
          "server_url=" +
          server_url +
          ", token=" +
          token +
          ", add_url=" +
          add_url
        );

        const thisIns = this;
        var output = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.SubmitFlag = false;
            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              thisIns.toast("success", output);
              thisIns.$router.push("/jci-award/judges/list");
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        var message = "";

        if (!this.MembershipId) {
          message += "Membership ID is required.\n";
        }
        if (!this.member) {
          message += "Member selection is required.\n";
        }
        if (!this.OrganizationType) {
          message += "Organization Type is required.\n";
        }
        if (!this.JudgeType) {
          message += "Judge Type is required.\n";
        }
        if (!this.ZoneCode) {
          message += "Zone Code is required.\n";
        }

        this.toast("error", message, false);
      }
    },
    getMemberRecords() {
      console.log("getMemberRecords called");

      var MembershipId = this.MembershipId;
      console.log({ MembershipId });

      this.OverlayLoadingFlag = true;
      this.SearchFlag = true;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/nominate-member-subscription/info";
      var upload = {
        UserInterface: 2,
        Membership: MembershipId,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );
      console.log("recordData:", this.recordData);

      const thisIns = this;
      var output = "";
      var records = {};
      var flag = 0;
      var data = {};

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.OverlayLoadingFlag = false;
          thisIns.SearchFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          console.log("output=" + output + ", flag=" + flag);

          records = response.data.records;
          console.log({ records });

          if (flag == 1) {
            thisIns.member = records;
            thisIns.toast("success", output);
          } else {
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.OverlayLoadingFlag = false;
          thisIns.SearchFlag = false;
        });
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss" scoped>
.page-container {
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
}

/* ============================================
   HEADER CARD
   ============================================ */
.header-card {
  border-radius: 20px !important;
  overflow: hidden;
  animation: slideDown 0.6s ease-out;
}

.header-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
    animation: rotate 20s linear infinite;
  }
}

.header-content {
  position: relative;
  z-index: 1;
}

.header-icon-box {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 15px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  animation: float 3s ease-in-out infinite;
}

.header-title {
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  animation: fadeInUp 0.8s ease-out;
}

.header-subtitle {
  opacity: 0.95;
  animation: fadeInUp 1s ease-out;
}

.action-btn {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 700;
  letter-spacing: 0.5px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3) !important;
  }
}

/* ============================================
   FORM CARD
   ============================================ */
.form-card {
  border-radius: 16px !important;
  animation: fadeIn 0.8s ease-out;
}

.form-header {
  display: flex;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 3px solid #667eea;
  margin-bottom: 24px;
}

.form-section {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%);
  border: 1px solid rgba(102, 126, 234, 0.1);
  border-radius: 12px;
  padding: 20px;
}

.field-label {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #004e92;
  margin-bottom: 8px;
  font-size: 14px;

  .text-danger {
    color: #ff6b6b;
    margin: 0 4px;
  }
}

.custom-input {
  ::v-deep .v-input__slot {
    border-radius: 12px !important;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
    }
  }

  ::v-deep &.v-input--is-focused .v-input__slot {
    border-color: #667eea !important;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
}

.search-btn {
  font-weight: 700;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4) !important;
  }
}

/* ============================================
   MEMBER PREVIEW CARD
   ============================================ */
.member-preview-card {
  border-radius: 16px !important;
  overflow: hidden;
  animation: slideInUp 0.6s ease-out;
  border: 2px solid #667eea;
}

.member-card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
}

.avatar-wrapper {
  position: relative;
  display: inline-block;
}

.member-avatar {
  border: 4px solid #667eea;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4);
  }
}

.avatar-badge {
  position: absolute;
  bottom: 5px;
  right: 5px;
  background: linear-gradient(135deg, #1db954 0%, #17a347 100%);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.member-info-section {
  animation: fadeInRight 0.8s ease-out;
}

.info-card {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  border-radius: 10px;
  border-left: 3px solid #667eea;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(5px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
  }
}

.info-label {
  font-size: 12px;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.info-value {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

/* ============================================
   ACTION BUTTONS
   ============================================ */
.submit-btn {
  font-weight: 700;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 20px rgba(29, 185, 84, 0.4) !important;
  }
}

.clear-btn {
  font-weight: 700;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 20px rgba(244, 67, 54, 0.4) !important;
  }
}

.gap-3 {
  gap: 16px;
}

/* ============================================
   ANIMATIONS
   ============================================ */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-10px);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 960px) {
  .header-content {
    text-align: center;

    .d-flex {
      flex-direction: column;
      align-items: center !important;
    }

    .text-right {
      text-align: center !important;
    }
  }

  .action-btn {
    margin: 4px 0 !important;
  }

  .member-info-section {
    text-align: center;
  }
}
</style>
